import { EnhancedScopeData } from '@ardoq/data-model';
import {
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  isComponentLabelFormatting,
  isReferenceLabelFormatting,
} from '@ardoq/api-types';
import {
  filterInterface,
  getLabelFormattingRules,
} from '@ardoq/filter-interface';
import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  componentUtils,
  fieldUtils,
  enhancedScopeDataOperations,
  workspaceUtils,
  referenceUtils,
  type FormatUserFieldFn,
} from '@ardoq/scope-data';
import { referenceInterface } from '@ardoq/reference-interface';
import { fieldInterface } from '@ardoq/field-interface';
import type { I18nLocale } from '@ardoq/i18n';
import { getGlobalReferenceType } from '@ardoq/renderers';
import { DEFAULT_COMPONENT_LABEL_FILTER } from '../consts';
import { referenceLabelOperations } from '@ardoq/reference-operations';
import { getAllReferenceLabelFilters } from '../labels/getAllReferenceLabelFilters';

/**
 * This is a temporary solution to make the transition from BB models to scope data easier.
 *
 * The next step is to create an object with functions that have the same signature as the model interface functions,
 * but are bound to the current scope data. This way we can use the scopeData or BB models interchangeably with minimal
 * refactoring. As soon BB models are no more, we shall pass the scope data instead of the interface object and use the
 * utility functions directly.
 *
 */

export type LabelsInterface = ReturnType<
  typeof getLabelsInterfaceWithModelInterfaces
>;

export const getLabelsInterfaceWithModelInterfaces = () => ({
  getLabelFormattingRules: filterInterface.getLabelFormatting,
  getComponentLabelFilter: filterInterface.getComponentLabelFilter,
  getAllReferenceLabelFilters: filterInterface.getAllReferenceLabelFilters,

  formatFieldValueForComponentOrReference:
    fieldInterface.formatFieldValueForComponentOrReference,

  getComponentWorkspaceId: componentInterface.getWorkspaceId,
  getComponentTypeName: componentInterface.getTypeName,
  getComponentFields: componentInterface.getFields,
  getComponentDisplayName: componentInterface.getDisplayName,
  getComponentDescription: (id: ArdoqId) =>
    componentInterface.get(id)?.description ?? null,
  getComponentDisplayLabel: componentInterface.getRawLabel,
  getComponentFieldValue: (id: ArdoqId, fieldName: string) =>
    componentInterface.get(id)?.[fieldName] ?? null,

  getReferenceDisplayText: (id: ArdoqId) =>
    referenceInterface.get(id)?.displayText ?? null,
  getReferenceRootWorkspaceId: (id: ArdoqId) =>
    referenceInterface.get(id)?.rootWorkspace ?? null,
  getGlobalReferenceType: referenceInterface.getGlobalReferenceType,
  getReferenceFields: referenceInterface.getFields,
  getReferenceDescription: (id: ArdoqId) =>
    referenceInterface.get(id)?.description ?? null,
  getReferenceFieldValue: (id: ArdoqId, fieldName: string) =>
    referenceInterface.get(id)?.[fieldName] ?? null,
  getLegacyReferenceDisplayLabel: referenceInterface.getDisplayLabel,

  getWorkspaceModelId: workspaceInterface.getWorkspaceModelId,

  getFieldByName: fieldInterface.getByName,
  getFieldLabel: fieldInterface.getLabel,
  getFieldsOfWorkspace: fieldInterface.getFieldsOfWorkspace,
  formatField: fieldInterface.format,
  getFieldData: fieldInterface.get,
  getFieldsByComponentType: fieldInterface.getFieldsByComponentType,
  getFieldsByReferenceType: fieldInterface.getFieldsByReferenceType,
});

export type LabelsContext = {
  enhancedScopeData: EnhancedScopeData;
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[];
  i18nLocale: I18nLocale;
  formatUserFieldFn?: FormatUserFieldFn;
};

export const getLabelsInterfaceForEnhancedScopeData = ({
  enhancedScopeData,
  formattingFilters,
  i18nLocale,
  formatUserFieldFn,
}: LabelsContext): LabelsInterface => ({
  // filterInterface
  getLabelFormattingRules: () => getLabelFormattingRules(formattingFilters),
  getComponentLabelFilter: () =>
    formattingFilters?.find(isComponentLabelFormatting) ||
    DEFAULT_COMPONENT_LABEL_FILTER,
  getAllReferenceLabelFilters: () =>
    getAllReferenceLabelFilters(formattingFilters),

  formatFieldValueForComponentOrReference: (
    fieldId: ArdoqId,
    modelId: ArdoqId
  ) =>
    fieldUtils.formatFieldValueForComponentOrReference(
      enhancedScopeData,
      fieldId,
      modelId,
      i18nLocale,
      formatUserFieldFn
    ),

  // componentInterface
  getComponentWorkspaceId: (componentId: string) =>
    componentUtils.getWorkspaceId(enhancedScopeData, componentId),
  getComponentTypeName: componentId =>
    componentUtils.getType(enhancedScopeData, componentId)?.name ?? null,
  getComponentFields: componentId =>
    Object.values(
      enhancedScopeDataOperations.getComponentFields(
        enhancedScopeData,
        componentId
      )
    ),
  getComponentDisplayName: componentId =>
    enhancedScopeDataOperations.getComponent(enhancedScopeData, componentId)
      ?.name ?? null,
  getComponentDescription: componentId =>
    enhancedScopeDataOperations.getComponent(enhancedScopeData, componentId)
      ?.description ?? null,
  getComponentDisplayLabel: (componentId: ArdoqId) =>
    componentUtils.getLabel(
      enhancedScopeData,
      formattingFilters,
      componentId,
      i18nLocale,
      formatUserFieldFn
    ),
  getComponentFieldValue: (componentId, fieldName) => {
    const component = enhancedScopeDataOperations.getComponent(
      enhancedScopeData,
      componentId
    );
    return component ? component[fieldName] : null;
  },

  // referenceInterface
  getReferenceDisplayText: referenceId =>
    enhancedScopeDataOperations.getReference(enhancedScopeData, referenceId)
      ?.displayText ?? null,
  getReferenceRootWorkspaceId: referenceId =>
    enhancedScopeDataOperations.getReference(enhancedScopeData, referenceId)
      ?.rootWorkspace ?? null,
  getGlobalReferenceType: referenceId =>
    getGlobalReferenceType(enhancedScopeData, referenceId),
  getReferenceFields: referenceId =>
    Object.values(
      enhancedScopeDataOperations.getReferenceFields(
        enhancedScopeData,
        referenceId
      )
    ),
  getReferenceDescription: referenceId =>
    enhancedScopeDataOperations.getReference(enhancedScopeData, referenceId)
      ?.description ?? null,
  getReferenceFieldValue: (referenceId: ArdoqId, fieldName: string) =>
    referenceUtils.getAttribute(enhancedScopeData, referenceId, fieldName),
  getLegacyReferenceDisplayLabel: (referenceId: ArdoqId) =>
    referenceLabelOperations.getLegacyReferenceDisplayLabel({
      referenceId,
      scopeData: enhancedScopeData,
      referenceLabelFormatting: formattingFilters?.find(
        isReferenceLabelFormatting
      ),
      i18nLocale,
    }),

  // workspaceInterface
  getWorkspaceModelId: workspaceId =>
    workspaceUtils.getWorkspaceModel(enhancedScopeData, workspaceId)?._id ??
    null,

  // fieldInterface
  getFieldByName: fieldName =>
    enhancedScopeData.fields.find(({ name }) => name === fieldName) ?? null,
  getFieldLabel: fieldId => fieldUtils.getLabel(enhancedScopeData, fieldId),
  getFieldsOfWorkspace: (workspaceId, fieldTypes) =>
    fieldUtils
      .getAllFieldsInWorkspace(enhancedScopeData, workspaceId)
      .filter(field => fieldTypes.includes(field.type)),
  formatField: ({ fieldId, value, excludeTime, fieldType, textAreaFormat }) =>
    fieldUtils.format({
      scopeData: enhancedScopeData,
      fieldId,
      value,
      excludeTime,
      fieldType,
      textAreaFormat,
      i18nLocale,
      formatUserFieldFn,
    }),
  getFieldData: fieldId => {
    return fieldUtils.getFieldData(enhancedScopeData, fieldId);
  },
  getFieldsByComponentType: (typeId: string, modelId: string) =>
    fieldUtils.getFieldsByComponentType(enhancedScopeData, { modelId, typeId }),
  getFieldsByReferenceType: (referenceTypeId: number, modelId: string) =>
    fieldUtils.getFieldsByReferenceType(enhancedScopeData, {
      modelId,
      typeId: String(referenceTypeId),
    }),
});
